// Utility functions for dynamically loading available latte images

/**
 * Dynamically checks which latte images are available by attempting to load them
 * @param startNum Starting image number (default: 1)
 * @param endNum Ending image number (default: 215)
 * @returns Promise that resolves to array of available image numbers
 */
export const getAvailableLatteImages = async (startNum: number = 1, endNum: number = 215): Promise<number[]> => {
  const availableImages: number[] = [];
  const imageCheckPromises: Promise<void>[] = [];

  for (let i = startNum; i <= endNum; i++) {
    const imageCheckPromise = new Promise<void>((resolve) => {
      const img = new Image();
      img.onload = () => {
        availableImages.push(i);
        resolve();
      };
      img.onerror = () => {
        // Image doesn't exist, just resolve without adding to available list
        resolve();
      };
      img.src = `/assets/img/latte/${i}.png`;
    });
    
    imageCheckPromises.push(imageCheckPromise);
  }

  // Wait for all image checks to complete
  await Promise.all(imageCheckPromises);
  
  // Sort the available images numerically
  return availableImages.sort((a, b) => a - b);
};

/**
 * Gets a random available latte image number
 * @param availableImages Array of available image numbers
 * @returns Random image number from available images
 */
export const getRandomLatteImage = (availableImages: number[]): number => {
  if (availableImages.length === 0) {
    // Fallback to image 1 if no images are available
    return 1;
  }
  return availableImages[Math.floor(Math.random() * availableImages.length)];
};

/**
 * Cache for available images to avoid repeated checks
 */
let cachedAvailableImages: number[] | null = null;
let cacheTimestamp: number | null = null;
const CACHE_DURATION = 5 * 60 * 1000; // 5 minutes

/**
 * Gets available latte images with caching
 * @param forceRefresh Force refresh the cache
 * @returns Promise that resolves to array of available image numbers
 */
export const getCachedAvailableLatteImages = async (forceRefresh: boolean = false): Promise<number[]> => {
  const now = Date.now();
  
  // Return cached result if available and not expired
  if (!forceRefresh && cachedAvailableImages && cacheTimestamp && (now - cacheTimestamp < CACHE_DURATION)) {
    return cachedAvailableImages;
  }
  
  // Refresh the cache
  cachedAvailableImages = await getAvailableLatteImages();
  cacheTimestamp = now;
  
  return cachedAvailableImages;
};